import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'app/store';
import { clearAlert, Alert } from 'app/alertSlice';

interface AlertProps {
  alert: Alert;
}

const AlertItem: React.FC<AlertProps> = ({ alert }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (alert.duration && alert.duration > 0) {
      const timer = setTimeout(() => {
        dispatch(clearAlert(alert.id));
      }, alert.duration);

      return () => clearTimeout(timer);
    }
  }, [alert, dispatch]);

  const handleClose = () => {
    dispatch(clearAlert(alert.id));
  };

  const getAlertClass = () => {
    const baseClass = 'alert alert-dismissible fade show';
    switch (alert.type) {
      case 'success':
        return `${baseClass} alert-success`;
      case 'error':
        return `${baseClass} alert-danger`;
      case 'warning':
        return `${baseClass} alert-warning`;
      case 'info':
        return `${baseClass} alert-info`;
      default:
        return `${baseClass} alert-primary`;
    }
  };

  const getIcon = () => {
    switch (alert.type) {
      case 'success':
        return <i className="bi bi-check-circle me-2"></i>;
      case 'error':
        return <i className="bi bi-exclamation-circle me-2"></i>;
      case 'warning':
        return <i className="bi bi-exclamation-triangle me-2"></i>;
      case 'info':
        return <i className="bi bi-info-circle me-2"></i>;
      default:
        return null;
    }
  };

  return (
    <div className={getAlertClass()} role="alert">
      <div className="d-flex align-items-center">
        {getIcon()}
        <span>{alert.message}</span>
      </div>
      <button
        type="button"
        className="btn-close"
        aria-label="Close"
        onClick={handleClose}
      ></button>
    </div>
  );
};

const AlertContainer: React.FC = () => {
  const alerts = useSelector((state: RootState) => state.alert.alerts);

  if (!alerts || alerts.length === 0) {
    return null;
  }

  return (
    <div
      className="position-fixed top-0 end-0 p-3"
      // Above Bootstrap modals (1055) and stacked modals so toast errors/success
      // are always visible on top of an open modal, not hidden behind it.
      style={{ zIndex: 2000, minWidth: '350px' }}
    >
      {alerts.map((alert) => (
        <AlertItem key={alert.id} alert={alert} />
      ))}
    </div>
  );
};

export default AlertContainer;